import './style.scss';

import { ColumnDef, getCoreRowModel, Row, useReactTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Input } from 'antd';
import React, { memo, useCallback, useDeferredValue, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { toEnglishLayout } from '@/shared/utils';

import { useArbitrageCexAccountsRecords } from 'hooks/tables';
import { useTypedSelector } from 'store';
import { RowComponent, StaticTable, TableHeaders, TBody } from 'tables/common';
import { IAccountBalances, ICexAccount } from 'types/accounts';
import { ICexPair } from 'types/pairs';
import { ICexAccountShortTableRow } from 'types/tables';
import { TableCheckbox } from 'ui/Table/TableCheckbox/TableCheckbox';
import { TableWrapper } from 'ui/TableWrapper/TableWrapper';
import { cexConfigs, ECexName } from 'web3/cexs';

interface IAccountWithBalance extends ICexAccount, Partial<IAccountBalances> {}

interface Props {
  pair: ICexPair;
  pairAccounts: IAccountWithBalance[];
  selectedAccountId?: number;
  onSelectAccount: (accountId?: number) => void;
}

const ArbitrageCexAccountsTable: React.FC<Props> = ({
  pair,
  pairAccounts,
  selectedAccountId,
  onSelectAccount,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const initialSelectedAccountIdRef = useRef(selectedAccountId);
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  const { tableData } = useArbitrageCexAccountsRecords(pairAccounts);
  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);

  const filteredTableData = useMemo(() => {
    const normalizedSearch = deferredSearchValue.trim().toLowerCase();
    let data = tableData;

    if (normalizedSearch) {
      data = data.filter(row => {
        const notesValue = row.notesCol?.value ?? '';
        return notesValue.toLowerCase().includes(normalizedSearch);
      });
    }

    // Sort initially selected account to top (only based on initial value)
    const initialId = initialSelectedAccountIdRef.current;
    if (initialId === undefined) return data;

    return [...data].sort((a, b) => {
      const aSelected = a.id === initialId;
      const bSelected = b.id === initialId;
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [tableData, deferredSearchValue]);

  const columns = useMemo<ColumnDef<ICexAccountShortTableRow>[]>(
    () =>
      (
        [
          {
            id: 'select',
            accessorFn: (row: Row<ICexAccountShortTableRow>) => row,
            header: ({ table }) => {
              const meta = table.options.meta as {
                searchValue?: string;
                onSearchChange?: (value: string) => void;
              };

              return (
                <div className="arbitrage-cex-accounts-table-header">
                  <span>Notes</span>
                  <Input
                    value={meta.searchValue ?? ''}
                    onChange={event => meta.onSearchChange?.(toEnglishLayout(event.target.value))}
                    allowClear
                    placeholder="Search"
                    className="arbitrage-cex-accounts-table-search"
                    size="small"
                  />
                </div>
              );
            },
            cell: ({
              row,
              table: {
                options: { meta },
              },
            }) => {
              const _meta = meta as any;

              return (
                <div className="checkbox-area">
                  {isAdmin && (
                    <TableCheckbox
                      id={uuid()}
                      checked={_meta.selectedAccountId === row.original.id}
                      indeterminate={row.getIsSomeSelected()}
                      onChange={() =>
                        onSelectAccount(
                          _meta.selectedAccountId !== row.original.id ? row.original.id : undefined,
                        )
                      }
                    />
                  )}
                  <span>{row.original.notesCol.value}</span>
                </div>
              );
            },
            size: 250,
          },
          {
            id: 'token_base',
            header: () => <div style={{ textAlign: 'left' }}>{pair.token_base.symbol}</div>,
            accessorFn: (row: ICexAccountShortTableRow) => row.tokenBaseCol,
            cell: memo(({ getValue }) => {
              const data = getValue() as string;

              return data;
            }),
            size: 100,
          },
          {
            id: 'token_quote',
            accessorFn: (row: ICexAccountShortTableRow) => row.tokenQuoteCol,
            header: () => <div style={{ textAlign: 'left' }}>{pair.token_quote.symbol}</div>,
            cell: memo(({ getValue }) => {
              const data = getValue() as string;

              return data;
            }),
            size: 100,
          },
          {
            id: 'cex',
            accessorFn: (row: ICexAccountShortTableRow) => row.cexCol,
            header: () => <div style={{ textAlign: 'right' }}>CEX</div>,
            cell: memo(({ getValue }) => {
              const cexName = getValue() as ECexName;

              return (
                <a
                  className="flex items-center justify-end gap-2"
                  href={cexConfigs?.[cexName]?.cexLink(
                    pair.token_base.symbol,
                    pair.token_quote.symbol,
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={cexConfigs?.[cexName]?.icon} alt={cexName} className="size-4" />
                  <span className="capitalize">{cexName}</span>
                </a>
              );
            }),
            size: 100,
          },
        ] as ColumnDef<ICexAccountShortTableRow>[]
      ).filter(el => el !== null),
    [isAdmin, onSelectAccount, pair.token_base.symbol, pair.token_quote.symbol],
  );

  const table = useReactTable({
    data: filteredTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: useMemo(
      () => ({
        selectedAccountId,
        searchValue,
        onSearchChange: (value: string) => setSearchValue(value),
      }),
      [selectedAccountId, searchValue],
    ),
  });

  const { rows } = table.getRowModel();
  const virtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: useMemo(() => rows.length, [rows]),
    overscan: 5,
    estimateSize: useCallback(() => 44, []),
  });

  return (
    <>
      <div className="arbitrage-cex-accounts-table-title">Accounts</div>
      <TableWrapper
        loading={false}
        records={pairAccounts}
        hasMore={false}
        heightLimit={400}
        cRef={tableContainerRef}
        className="arbitrage-cex-accounts-table-container"
      >
        <StaticTable className="mm-common-table">
          <TableHeaders
            isAllChecked={table.getIsAllRowsSelected()}
            table={table}
            update={searchValue}
          />
          <TBody
            paddingTop={virtualizer.getVirtualItems()?.[0]?.start || 0}
            paddingBottom={
              virtualizer.getTotalSize() -
              (virtualizer.getVirtualItems()?.[virtualizer.getVirtualItems().length - 1]?.end || 0)
            }
          >
            {virtualizer.getVirtualItems().map(virtualRow => {
              const row = rows[virtualRow.index];

              return (
                <RowComponent
                  isChecked={selectedAccountId === row.original.id}
                  row={row}
                  height={virtualRow.size}
                  key={virtualRow.index}
                />
              );
            })}
          </TBody>
        </StaticTable>
      </TableWrapper>
    </>
  );
};

export { ArbitrageCexAccountsTable };
